import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import ChartContext from '../ChartContext';
import usePrevious from '../UsePrevious';
import { validChartTypes } from '../../utils/propTypeValidators';
import createProvidedChart from './createProvidedChart';

var noop = function noop(c) {
  return c;
};

var BaseChart = function BaseChart(_ref) {
  var _ref$children = _ref.children,
      children = _ref$children === void 0 ? null : _ref$children,
      _ref$callback = _ref.callback,
      callback = _ref$callback === void 0 ? noop : _ref$callback,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      restProps = _objectWithoutProperties(_ref, ["children", "callback", "className"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      rendered = _useState2[0],
      setRendered = _useState2[1];

  var domNodeRef = useRef(null);
  var chartRef = useRef(null);
  var providedChartRef = useRef(null);
  useLayoutEffect(function () {
    var myChart = initHighcharts(restProps, domNodeRef.current);
    chartRef.current = myChart;
    providedChartRef.current = createProvidedChart(myChart, restProps.chartType);
    callback(myChart);
    setRendered(true);
  }, []);
  useEffect(function () {
    return function () {
      var myChart = chartRef.current;

      if (myChart) {
        // Fixes #14
        window.requestAnimationFrame(myChart.destroy.bind(myChart));
        myChart.__destroyed = true;
      }
    };
  }, []);
  var prevProps = usePrevious(restProps);
  useEffect(function () {
    if (!rendered) return;
    var plotOptions = restProps.plotOptions;
    var myChart = chartRef.current;

    if (Object.is(prevProps.plotOptions, plotOptions) === false && myChart) {
      myChart.update({
        plotOptions: plotOptions
      }, false);
      providedChartRef.current.needsRedraw();
    }
  });
  return React.createElement("div", {
    className: "chart ".concat(className),
    ref: domNodeRef
  }, rendered && React.createElement(ChartContext.Provider, {
    value: providedChartRef.current
  }, children));
};

var initHighcharts = function initHighcharts(props, domNode) {
  if (!domNode) {
    return;
  }

  var chartCreationFunc = props.chartCreationFunc,
      callback = props.callback,
      chart = props.chart,
      polar = props.polar,
      gauge = props.gauge,
      _props$styledMode = props.styledMode,
      styledMode = _props$styledMode === void 0 ? false : _props$styledMode,
      children = props.children,
      rest = _objectWithoutProperties(props, ["chartCreationFunc", "callback", "chart", "polar", "gauge", "styledMode", "children"]);

  var opts = _objectSpread({
    chart: _objectSpread({
      styledMode: styledMode
    }, chart),
    title: {
      text: null
    },
    subtitle: {
      text: null
    },
    legend: {
      enabled: false
    },
    rangeSelector: {
      enabled: false
    },
    navigator: {
      enabled: false
    },
    scrollbar: {
      enabled: false
    },
    tooltip: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    series: [],
    xAxis: [],
    yAxis: []
  }, rest);

  var myChart = chartCreationFunc(domNode, opts);
  myChart.polar = polar;
  myChart.angular = gauge;
  return myChart;
};

export default BaseChart;